import React from 'react';

import {assertDataViewActionIsConstant} from 'analysis-tool/data-view/DataViewContext';
import {OverflowDataViewAction} from 'analysis-tool/data-view/DataViewContext';
import {trackEvent} from 'analytics/Analytics';
import {pluralize} from 'BenchlingUtil';
import {ActionOrigin, DATA_FRAMES_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import makeDataFrameExportString from 'data-frames/util/makeDataFrameExportString';
import Option from 'ui-library/menus/Option';
import copyToClipboard from 'util/clipboard/copyToClipboard';

import {OverflowActionRenderFn, ToolbarActionRenderParams} from '../DataFrameActionToolbar';

function copyDataFrameToClipboard(dataFrame: DataFrame, selectedRowIds: Set<string>): void {
  // Download as TEXT to be consistent with downloads, and avoid a dependency on hydrated properties.
  copyToClipboard(makeDataFrameExportString(dataFrame, selectedRowIds, DataFrameTableMode.TEXT, '\t'), {
    showToastOnSuccess: true,
    toastText:
      selectedRowIds.size === 0
        ? 'Copied table to clipboard.'
        : `Copied ${selectedRowIds.size} ${pluralize(selectedRowIds.size, 'row')} to clipboard.`,
  });
}

/**
 * TODO(BNCH-123685): Move to analysis-tool/data-view/ module once `renderCopyToClipboardOption` is removed.
 */
export function getCopyToClipboardAction(
  dataFrame: DataFrame,
  analyticsData: {
    dataFrameId?: string;
    actionOrigin: ActionOrigin;
  },
  selectedRowIds: Set<string>,
  isResolvingSelectedRows?: boolean,
  hasErrorLoadingSelectedRows?: boolean
): OverflowDataViewAction {
  const numSelectedRows = selectedRowIds.size;
  let helpText: string | undefined;
  if (isResolvingSelectedRows) {
    helpText = 'Loading selected rows…';
  } else if (hasErrorLoadingSelectedRows) {
    helpText = "Couldn't load some selected rows.";
  }
  return {
    icon: 'copy',
    label:
      numSelectedRows === 0
        ? 'Copy to clipboard'
        : `Copy ${numSelectedRows} ${pluralize(numSelectedRows, 'row')} to clipboard`,
    isDisabled: Boolean(isResolvingSelectedRows || hasErrorLoadingSelectedRows),
    helpText,
    onClick: () => {
      copyDataFrameToClipboard(dataFrame, selectedRowIds);
      trackEvent(DATA_FRAMES_ANALYTICS_ACTIONS.EXPORTED_DATA_FRAME, {
        data_frame_id: analyticsData.dataFrameId,
        action_origin: analyticsData.actionOrigin,
        type: 'clipboard',
        num_rows_selected: selectedRowIds.size,
        total_columns: dataFrame.columns.length,
        total_rows: dataFrame.rows.length,
      });
    },
  };
}

/**
 * @deprecated TODO(BNCH-123685): Remove once uses of DataFrameTableWrapper are removed.
 */
export const renderCopyToClipboardOption: OverflowActionRenderFn = (
  dataFrame: DataFrame,
  params: Pick<
    ToolbarActionRenderParams,
    | 'dataFrameId'
    | 'selectedRowIds'
    | 'actionOrigin'
    | 'isResolvingSelectedRows'
    | 'hasErrorLoadingSelectedRows'
  >
) => {
  const option = getCopyToClipboardAction(
    dataFrame,
    {
      dataFrameId: params.dataFrameId,
      actionOrigin: params.actionOrigin,
    },
    params.selectedRowIds,
    params.isResolvingSelectedRows,
    params.hasErrorLoadingSelectedRows
  );
  assertDataViewActionIsConstant(option);

  return (
    <Option
      icon={option.icon}
      isDisabled={option.isDisabled}
      key="CopyToClipboardOption"
      onSelect={option.onClick}
      tooltipTitle={option.helpText}
    >
      {option.label}
    </Option>
  );
};
